import React, { useState, useEffect, useRef } from 'react';

// Erweiterte Typen für präzise Steuerung
export type FieldVariant = 'standard' | 'revenue' | 'cost' | 'wage' | 'hours' | 'days' | 'percent';

interface EditableFieldProps {
  value: any;
  onChange?: (val: any) => void;
  readOnly?: boolean;
  // 'type' steuert eher das technische Input-Verhalten, 'variant' die Business-Logik
  type?: 'text' | 'number' | 'currency' | 'percentage'; 
  variant?: FieldVariant; // NEU: Steuert Dezimalstellen & Formatierung zentral
  className?: string;
  placeholder?: string;
  suffix?: string;       
  currency?: string;     
  decimalPlaces?: number; // Kann immer noch als manueller Override genutzt werden
  tooltip?: string; 
  transparent?: boolean; 
  [key: string]: any;
}

const EditableField: React.FC<EditableFieldProps> = ({
  value,
  onChange,
  readOnly = false,
  type = 'text',
  variant = 'standard', // Default
  className = '',
  placeholder = '',
  suffix: manualSuffix,
  currency = 'EUR',
  decimalPlaces,
  tooltip,
  transparent = false,
  ...rest
}) => {
  const [localValue, setLocalValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  // 🧠 ZENTRALE LOGIK: Dezimalstellen basierend auf Business-Regeln
  const getActiveDecimals = (): number => {
    // 1. Manueller Override gewinnt immer
    if (decimalPlaces !== undefined) return decimalPlaces;

    // 2. Business-Regeln (Deine Vorgabe)
    switch (variant) {
        case 'revenue': // Umsätze
        case 'cost':    // Kosten
        case 'days':    // Tage
            return 0;
        case 'wage':    // Löhne
            return 2;
        case 'hours':   // Stunden
        case 'percent': // Prozente
            return 1;
        default:
            // Fallback Logik basierend auf Suffix/Type, falls keine Variant gesetzt
            if (manualSuffix === 'Tage') return 0;
            if (manualSuffix === 'Std.') return 1;
            if (type === 'percentage') return 1;
            return 2; // Standard Geld/Zahl
    }
  };

  const activeDecimals = getActiveDecimals();

  // FORMATIERUNGS-ENGINE
  const formatNumber = (val: any, decimals: number) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = parseFloat(String(val).replace(',', '.'));
    if (isNaN(num)) return val;
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // SYNCHRONISATION
  useEffect(() => {
    if (readOnly) {
      setLocalValue(formatNumber(value, activeDecimals));
    } else {
      if (document.activeElement !== inputRef.current) {
         if(type === 'number' || type === 'currency' || type === 'percentage') {
             setLocalValue(formatNumber(value, activeDecimals));
         } else {
             setLocalValue(value === null || value === undefined ? '' : value);
         }
      }
    }
  }, [value, readOnly, activeDecimals, type]);

  // HANDLER
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocalValue(e.target.value);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!readOnly) e.target.select();
    if (rest.onFocus) rest.onFocus(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') inputRef.current?.blur();
    if (rest.onKeyDown) rest.onKeyDown(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onChange && !readOnly) {
      let saveValue = localValue.replace(/\./g, '').replace(',', '.');
      
      if (saveValue === '' && type !== 'text') {
         onChange(0);
         setLocalValue(formatNumber(0, activeDecimals));
      } else if (!isNaN(parseFloat(saveValue)) && type !== 'text') {
         let num = parseFloat(saveValue);
         if (variant === 'days' || manualSuffix === 'Tage') num = Math.round(num); // Tage runden
         onChange(num);
         setLocalValue(formatNumber(num, activeDecimals));
      } else {
         onChange(localValue);
      }
    }
    if (rest.onBlur) rest.onBlur(e);
  };

  // SUFFIX LOGIK
  const getSuffix = () => {
    if (manualSuffix) return manualSuffix;
    if (type === 'currency') return currency === 'CHF' ? 'CHF' : 'EUR';
    if (type === 'percentage') return '%';
    return null;
  };
  const displaySuffix = getSuffix();

  // DESIGN & FARBEN
  const baseContainer = "relative flex items-center w-full h-full min-h-[32px] rounded-md transition-colors overflow-visible group/field"; // overflow-visible für Tooltip!
  
  let modeClasses = "";
  let textColor = "";
  let suffixColor = "";

  if (transparent) {
    modeClasses = "bg-transparent border-none";
    textColor = "text-inherit"; 
    suffixColor = "text-current opacity-60"; 
  } else {
    modeClasses = readOnly
      ? "bg-stone-100 dark:bg-[#0a0a0a] border border-stone-300 dark:border-white/10 cursor-default" 
      : "bg-white dark:bg-[#0a0a0a] border border-stone-300 dark:border-white/10 focus-within:ring-1 focus-within:ring-[#E6C299] focus-within:border-[#E6C299] hover:border-stone-400 dark:hover:border-white/20"; 
    // Light mode: bronze (#8D6435) — #E6C299 washes out on pale backgrounds
    textColor = readOnly ? "text-[#8D6435] dark:text-[#E6C299]" : "text-stone-900 dark:text-white";
    suffixColor = readOnly ? "text-[#8D6435]/75 dark:text-[#E6C299]/50" : "text-stone-500 dark:text-slate-400";
  }

  const inputClasses = "w-full h-full bg-transparent border-none text-right font-mono tabular-nums text-sm focus:ring-0 focus:outline-none placeholder-slate-600 selection:bg-blue-600 selection:text-white";
  
  return (
    <div className={`${baseContainer} ${modeClasses} ${className}`}>
      {/* 🧠 INTEGRIERTER TOOLTIP (Beige/Gold) */}
      {tooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover/field:block min-w-[200px] max-w-[300px] bg-[#E6C299] text-slate-900 text-xs p-3 rounded-md shadow-2xl border border-[#dcb07b] z-[100] pointer-events-none text-left leading-relaxed font-medium font-sans">
            <div className="relative z-50">{tooltip}</div>
            {/* Pfeil unten */}
            <div className="absolute left-1/2 -bottom-1 w-2 h-2 bg-[#E6C299] transform -translate-x-1/2 rotate-45 border-r border-b border-[#dcb07b]"></div>
        </div>
      )}

      <input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        readOnly={readOnly}
        placeholder={placeholder}
        className={`${inputClasses} ${textColor} pl-2 ${displaySuffix ? 'pr-10' : 'pr-2'}`}
        {...rest}
        inputMode={
          rest.inputMode ??
          (type === 'number' || type === 'currency' || type === 'percentage' ? 'decimal' : 'text')
        }
        enterKeyHint={rest.enterKeyHint ?? (type === 'text' ? 'enter' : 'done')}
        autoComplete={rest.autoComplete ?? 'off'}
        lang={rest.lang ?? 'de-DE'}
      />
      
      {displaySuffix && (
        <div className={`absolute right-2 top-1/2 -translate-y-1/2 font-sans text-xs font-medium pointer-events-none select-none ${suffixColor}`}>
          {displaySuffix}
        </div>
      )}
    </div>
  );
};

export default EditableField;